<template>
  <!-- A plan ladder: which plan each capability starts on, read from data/pricing-features.json
       (chapter.groups lists matrix row keys; labels come from the matrix unless the chapter
       renames them). It is a real table: each row's bar spans from its first plan to Enterprise.
       When the table scrolls into view the bars grow out from their first plan, row by row; the
       server renders the final frame. On phones each row reads "From <plan>" above its track. -->
  <section class="sld">
    <div class="sld-shell">
      <header class="sld-heading">
        <div>
          <h2>{{ chapter.title }}</h2>
        </div>
        <div>
          <p class="sld-intro">{{ chapter.intro }}</p>
          <NuxtLink :to="localePath('/pricing')" class="sld-link">
            {{ chapter.linkLabel }} <span aria-hidden="true">-&gt;</span>
          </NuxtLink>
        </div>
      </header>

      <div ref="rootEl" class="sld-card" :class="{ 'is-run': running || armed, 'is-armed': armed }">
        <table class="sld-table">
          <caption class="sld-sr">{{ chapter.caption }}</caption>
          <thead>
            <tr>
              <th scope="col" class="sld-feature-col"><span class="sld-sr">{{ chapter.featureLabel }}</span></th>
              <th v-for="plan in plans" :key="plan.slug" scope="col" class="sld-plan-col">
                <strong translate="no">{{ plan.name }}</strong>
                <small>${{ plan.monthly }}/mo</small>
              </th>
            </tr>
          </thead>
          <tbody v-for="(group, g) in groups" :key="group.title">
            <tr class="sld-group">
              <th :colspan="plans.length + 1" scope="colgroup">{{ group.title }}</th>
            </tr>
            <tr v-for="(row, r) in group.rows" :key="row.key" class="sld-row" :style="{ '--d': `${0.1 + (g * 4 + r) * 0.07}s` }">
              <th scope="row">{{ row.label }}</th>
              <td v-for="n in row.start" :key="n" class="sld-empty"><span class="sld-sr">{{ chapter.no }}</span></td>
              <td :colspan="plans.length - row.start" class="sld-span" :style="{ '--span': plans.length - row.start }">
                <span class="sld-bar" :style="{ '--tone': group.tone }">
                  <span class="sld-from">{{ chapter.fromLabel }} <b translate="no">{{ plans[row.start].name }}</b></span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="sld-foot">{{ chapter.note }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { pricingPlans, pricingRow, firstPlanIndex } from "~/utils/pricingGates";

const props = defineProps({
  chapter: { type: Object, required: true },
});

const { localePath } = useWorkspaceLocale();
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.25 });

const plans = pricingPlans;
const tones = ["#0e7490", "#3f58bf", "#7948af", "#2d7a4e"];
const groups = computed(() =>
  props.chapter.groups.map((group, g) => ({
    title: group.title,
    tone: group.tone || tones[g % tones.length],
    rows: group.rows.map((item) => {
      const key = typeof item === "string" ? item : item.key;
      return {
        key,
        label: (typeof item === "object" && item.label) || pricingRow(key).label,
        start: firstPlanIndex(key),
      };
    }),
  })),
);
</script>

<style scoped>
.sld { background: #f8fafc; padding: 6.5rem 0; }
.dark .sld { background: #0b1220; }
.sld-shell { width: min(100% - 3rem, 84rem); margin: 0 auto; }
.sld-heading { display: grid; gap: 1.25rem; align-items: end; }
@media (min-width: 1024px) { .sld-heading { grid-template-columns: 1fr 1fr; gap: 2.5rem; } }
.sld-heading h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .sld-heading h2 { color: #f8fafc; }
.sld-intro { color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .sld-intro { color: #cbd5e1; }
.sld-link { display: inline-flex; gap: 0.4rem; margin-top: 1rem; color: #0e7490; font-weight: 800; }
.dark .sld-link { color: #67e8f9; }
.sld-link:hover { text-decoration: underline; }

.sld-card { margin-top: 3rem; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; padding: 1.25rem 1.5rem 1rem; }
.dark .sld-card { border-color: #1e293b; background: #0f172a; }
.sld-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.sld-feature-col { width: 30%; }
.sld-plan-col { padding: 0 0.25rem 0.75rem; text-align: left; vertical-align: bottom; }
.sld-plan-col strong { display: block; color: #0f172a; font-size: 0.92rem; font-weight: 800; }
.dark .sld-plan-col strong { color: #f1f5f9; }
.sld-plan-col small { color: #64748b; font-size: 0.78rem; font-weight: 600; }
.dark .sld-plan-col small { color: #94a3b8; }
.sld-group th { padding: 1rem 0 0.35rem; color: #64748b; font-size: 0.74rem; font-weight: 800; text-align: left; text-transform: uppercase; letter-spacing: 0.02em; }
.dark .sld-group th { color: #94a3b8; }
.sld-row th { padding: 0.45rem 1rem 0.45rem 0; color: #0f172a; font-size: 0.92rem; font-weight: 650; text-align: left; line-height: 1.3; }
.dark .sld-row th { color: #e2e8f0; }
.sld-row td { padding: 0.45rem 0; border-left: 1px dashed #e2e8f0; }
.dark .sld-row td { border-left-color: #1e293b; }
.sld-row + .sld-row th, .sld-row + .sld-row td { border-top: 1px solid #f1f5f9; }
.dark .sld-row + .sld-row th, .dark .sld-row + .sld-row td { border-top-color: #172033; }
.sld-bar {
  display: flex;
  align-items: center;
  height: 28px;
  margin: 0 0.25rem;
  border-radius: 4px;
  background: color-mix(in srgb, var(--tone) 14%, transparent);
  box-shadow: inset 3px 0 0 var(--tone);
  padding: 0 0.6rem;
  transform-origin: left center;
}
.dark .sld-bar { background: color-mix(in srgb, var(--tone) 30%, transparent); box-shadow: inset 3px 0 0 color-mix(in srgb, var(--tone) 60%, #fff); }
.sld-from { overflow: hidden; color: #334155; font-size: 0.78rem; white-space: nowrap; text-overflow: ellipsis; }
.dark .sld-from { color: #e2e8f0; }
.sld-from b { font-weight: 800; }
.sld-foot { margin-top: 1rem; color: #64748b; font-size: 0.82rem; line-height: 1.55; }
.dark .sld-foot { color: #94a3b8; }
.sld-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

.is-run .sld-bar { animation: sld-grow 0.8s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .sld-from { animation: sld-fade 0.4s ease-out calc(var(--d) + 0.5s) both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes sld-grow { from { transform: scaleX(0.04); } to { transform: none; } }
@keyframes sld-fade { from { opacity: 0; } to { opacity: 1; } }

/* Phones: the plan header goes; each row is its label, then a five-step track with the bar. */
@media (max-width: 767px) {
  .sld { padding: 4.5rem 0; }
  .sld-shell { width: min(100% - 2rem, 84rem); }
  .sld-card { margin-top: 2rem; padding: 0.75rem 0.9rem; }
  .sld-table thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
  .sld-table, .sld-table tbody { display: block; }
  .sld-group { display: block; }
  .sld-group th { display: block; padding-top: 0.9rem; }
  .sld-row { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); padding: 0.5rem 0; }
  .sld-row + .sld-row { border-top: 1px solid #f1f5f9; }
  .dark .sld-row + .sld-row { border-top-color: #172033; }
  .sld-row th { grid-column: 1 / -1; padding: 0 0 0.35rem; border: 0 !important; }
  .sld-row td { display: block; padding: 0; border: 0 !important; }
  .sld-empty { height: 26px; border-radius: 4px; background: #f1f5f9 !important; margin-right: 3px; }
  .dark .sld-empty { background: #172033 !important; }
  .sld-span { grid-column: span var(--span, 1); }
  .sld-bar { height: 26px; margin: 0; }
}
@media (prefers-reduced-motion: reduce) { .sld-card * { animation: none !important; } }
</style>
